/**
 * Zelium AI — Color Palette
 * =========================
 * SINGLE SOURCE OF TRUTH for all raw color values.
 *
 * Rules:
 * - Every hex / rgba colour used anywhere in the app is declared here.
 * - CSS custom properties in `zelium-tokens.css` mirror these values.
 * - TypeScript/TSX files import from here — never hardcode hex strings.
 * - To retheme Zelium, change values here and update zelium-tokens.css.
 *
 * Structure:
 *   BRAND_COLORS    — core palette (accent, primary surfaces, white)
 *   SEMANTIC_COLORS — meaningful status colours (score tiers, status chips)
 *   GRADE_COLORS    — report card letter grades (A–F), light + dark mode
 *   CHART_COLORS    — data visualisation lines, grids, labels
 *   UI_COLORS       — hero text, glass effects, glows, callout boxes
 *   CONFETTI_COLORS — celebration confetti colour array
 *   COLORS          — nested convenience export (brand / semantic / …)
 *
 * Palette: "Deep Intelligence"
 *   Violet  (#8B5CF6) = Zelium brand identity — AI intelligence, premium, trust
 *   Cyan    (#0891B2) = precision action / data CTA — AI's domain, technical clarity
 *   Navy    (#0B1220) = authority surface — deep, focused, serious analytics
 *   Score tiers remain traffic-light (red→amber→green) + cyan for Excellent
 */

/* ─── Brand ─────────────────────────────────────────────────── */

export const BRAND_COLORS = {
  /** Violet accent — badges, borders, accents, progress fills, primary buttons. */
  accent:           '#8B5CF6',
  /** Deeper violet — gradient end-stop, arrow labels, history boost. */
  accentStrong:     '#7C3AED',
  /** Lighter violet — accent strong in dark mode. */
  accentStrongDark: '#A78BFA',
  /** Lightest violet — tier badge text / subtle labels on dark hero backgrounds. */
  accentLight:      '#C4B5FD',
  /** Cyan CTA (light mode) — precision action, chart line, AI-needs callout. */
  ctaBlue:          '#0891B2',
  /** Cyan CTA (dark mode). */
  ctaBlueDark:      '#22D3EE',
  /** Bright cyan — badges, borders, glows, score-excellent indicator. Matches website --color-cyan-bright. */
  cyanBright:       '#06B6D4',
  /** Primary dark surface / hero bg mid-stop. */
  primaryDark:      '#111827',
  /** Darkest hero background. Matches website --color-bg. */
  heroBgDeep:       '#0B1220',
  /** Mid-dark raised surface. Matches website --color-surface-raised. */
  heroBgMid:        '#1E293B',
  /** Alias for heroBgMid — raised dark surface. */
  surfaceRaised:    '#1E293B',
  /** Off-white page background / text-on-dark. Matches website --color-text. */
  offWhite:         '#F9FAFB',
  /** Subdued text — secondary text on light and dark surfaces. Matches website --color-subdued. */
  textSubdued:      '#94A3B8',
  /** Muted text — lowest-contrast foreground, disabled labels. Matches website --color-muted. */
  textMuted:        '#64748B',
  /** Light bg mid tone — gradient second stop. */
  lightBgMid:       '#F1F5F9',
  /** Light bg pale tone — gradient end stop. */
  lightBgPale:      '#E2E8F0',
  /** Pure white — surfaces, button text. */
  white:            '#FFFFFF',
} as const;

/* ─── Semantic / Status ─────────────────────────────────────── */
/**
 * Score tiers use the universal traffic-light progression (red → amber → green),
 * elevated to cyan for the top tier — cyan = "AI's domain / digital precision."
 * Brand accent (violet) is intentionally separate from score data colours.
 *
 * After editing, also update the matching tokens in zelium-tokens.css:
 *   --z-score-critical, --z-score-warning, --z-score-good, --z-score-excellent
 *   --z-status-success-text, --z-status-error-text, --z-status-warning-text
 *   --z-status-info-text, --z-status-*-bg
 */
export const SEMANTIC_COLORS = {
  // ── Score tier foreground colours ───────────────────────────
  /** Red  — score 0–39  "AI-Invisible" */
  critical:         '#EF4444',
  /** Amber — score 40–69 "AI-Confusing" */
  warning:          '#F59E0B',
  /** Green — score 70–89 "AI-Readable" */
  good:             '#22C55E',
  /** Cyan — score 90–100 "AI-Discoverable" (distinct from brand violet) */
  excellent:        '#06B6D4',

  // ── Status text colours — WCAG AA on white backgrounds ──────
  /** Dark green text — present / success states. */
  successText:      '#166534',
  /** Dark red text   — missing / error states. */
  errorText:        '#991b1b',
  /** Dark amber text — incomplete / warning states. */
  warningText:      '#92400e',
  /** Violet text — info / high-impact states (matches brand accent). */
  infoText:         '#7C3AED',

  // ── Status background colours (muted alpha fills) ───────────
  successBg:        'rgba(34,  197, 94,  0.10)',
  errorBg:          'rgba(239, 68,  68,  0.10)',
  warningBg:        'rgba(245, 158, 11,  0.10)',
  infoBg:           'rgba(124, 58,  237, 0.08)',
  /** Cyan tint — FieldDiff "After:" panel background (matches border-info blue). */
  diffAfterBg:      'rgba(8,   145, 178, 0.08)',
} as const;

/* ─── Grade Scale ───────────────────────────────────────────── */

export const GRADE_COLORS = {
  // Light mode
  A: '#2e7d32',
  B: '#558b2f',
  C: '#f9a825',
  D: '#ef6c00',
  F: '#c62828',
  // Dark mode
  ADark: '#4ade80',
  BDark: '#86efac',
  CDark: '#fde047',
  DDark: '#fb923c',
  FDark: '#f87171',
} as const;

/* ─── Chart ─────────────────────────────────────────────────── */

export const CHART_COLORS = {
  gridLight:        '#e1e3e5',
  labelLight:       '#6d7175',
  lineLight:        '#0891B2',   // Cyan — data precision line
  areaLight:        'rgba(8, 145, 178, 0.10)',
  gridDark:         '#374151',
  labelDark:        '#9CA3AF',
  lineDark:         '#22D3EE',   // Cyan light — dark mode chart
  areaDark:         'rgba(34, 211, 238, 0.10)',
} as const;

/* ─── Hero / Glass / Overlay UI ─────────────────────────────── */

export const UI_COLORS = {
  /** Text colour over dark hero backgrounds. */
  textOnDark:         '#F9FAFB',
  /** Muted slate subtitle text inside hero sections. */
  heroSubtitle:       '#94A3B8',
  /** Violet radial glow behind hero (very subtle — corner blobs). */
  heroGlowAccent:     'rgba(139, 92, 246, 0.10)',
  /** Cyan radial glow behind hero (very subtle — corner blobs). */
  heroGlowCta:        'rgba(8, 145, 178, 0.08)',
  /** Violet layered background radial — stronger, used in hero card bg. */
  heroRadialAccent:   'rgba(139, 92, 246, 0.22)',
  /** Cyan layered background radial — used in hero card bg. */
  heroRadialCta:      'rgba(6, 182, 212, 0.12)',
  /**
   * Glass surface / button background on dark hero backdrops.
   * Aligns with website --color-glass (rgba 255,255,255,0.04).
   */
  glassBg:            'rgba(255, 255, 255, 0.04)',
  /** Glass surface background on hover. Matches website --color-glass-hover. */
  glassBgHover:       'rgba(255, 255, 255, 0.07)',
  /** Glass surface border. Matches website --color-glass-border. */
  glassBorder:        'rgba(255, 255, 255, 0.09)',
  /** Glass surface border on hover. */
  glassBorderHover:   'rgba(255, 255, 255, 0.15)',
  /** Primary (accent) button glow on hover. */
  btnAccentGlow:      'rgba(139, 92, 246, 0.35)',
  /** "What AI needs →" callout box background. */
  fieldIdealBg:       'rgba(6, 182, 212, 0.06)',
  /** "What AI needs →" callout box border (solid). */
  fieldIdealBorder:   '#06B6D4',
} as const;

/* ─── Confetti ───────────────────────────────────────────────── */

export const CONFETTI_COLORS = [
  BRAND_COLORS.accent,          // Violet
  BRAND_COLORS.accentStrong,    // Deep violet
  BRAND_COLORS.ctaBlue,         // Cyan
  SEMANTIC_COLORS.excellent,    // Cyan light
  SEMANTIC_COLORS.good,         // Green
  BRAND_COLORS.white,           // White sparks
] as const;

/* ─── Convenience nested export ─────────────────────────────── */

export const COLORS = {
  brand:    BRAND_COLORS,
  semantic: SEMANTIC_COLORS,
  grade:    GRADE_COLORS,
  chart:    CHART_COLORS,
  ui:       UI_COLORS,
  confetti: CONFETTI_COLORS,
} as const;
